import { useState } from 'react';
import { ChevronRight } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import type { DailyLogForm } from '../types';

interface SensorDailyLogFieldsProps {
  dailylog: DailyLogForm;
  onChange: (field: string, value: DailyLogForm) => void;
}

const HOUR_OPTIONS = Array.from({ length: 24 }, (_, i) => ({
  value: String(i),
  label: `${String(i).padStart(2, '0')}:00 UTC`,
}));

const NONE_VALUE = '__none__';

export function SensorDailyLogFields({
  dailylog,
  onChange,
}: SensorDailyLogFieldsProps) {
  const [open, setOpen] = useState(false);

  const update = (key: keyof DailyLogForm, value: string) => {
    onChange('dailylog', { ...dailylog, [key]: value });
  };

  const hasSchedule = dailylog.am !== '' || dailylog.pm !== '';

  return (
    <Collapsible open={open} onOpenChange={setOpen}>
      <CollapsibleTrigger className="flex items-center gap-1 text-sm font-medium text-muted-foreground hover:text-foreground w-full py-1">
        <ChevronRight
          className={`h-4 w-4 transition-transform ${open ? 'rotate-90' : ''}`}
        />
        Daily Log Schedule{hasSchedule ? ' (configured)' : ''}
      </CollapsibleTrigger>
      <CollapsibleContent className="space-y-3 pt-2 pl-5">
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label htmlFor="dailylog-am">AM Hour (UTC)</Label>
            <Select
              value={dailylog.am || NONE_VALUE}
              onValueChange={(v) => update('am', v === NONE_VALUE ? '' : v)}
            >
              <SelectTrigger id="dailylog-am">
                <SelectValue placeholder="Not set" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={NONE_VALUE}>Not set</SelectItem>
                {HOUR_OPTIONS.filter((h) => Number(h.value) < 12).map((h) => (
                  <SelectItem key={h.value} value={h.value}>
                    {h.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label htmlFor="dailylog-pm">PM Hour (UTC)</Label>
            <Select
              value={dailylog.pm || NONE_VALUE}
              onValueChange={(v) => update('pm', v === NONE_VALUE ? '' : v)}
            >
              <SelectTrigger id="dailylog-pm">
                <SelectValue placeholder="Not set" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={NONE_VALUE}>Not set</SelectItem>
                {HOUR_OPTIONS.filter((h) => Number(h.value) >= 12).map((h) => (
                  <SelectItem key={h.value} value={h.value}>
                    {h.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label htmlFor="dailylog-threshold-min">Threshold Min</Label>
            <Input
              id="dailylog-threshold-min"
              type="number"
              value={dailylog.threshold_min}
              onChange={(e) => update('threshold_min', e.target.value)}
              placeholder="Lower bound"
            />
          </div>
          <div>
            <Label htmlFor="dailylog-threshold-max">Threshold Max</Label>
            <Input
              id="dailylog-threshold-max"
              type="number"
              value={dailylog.threshold_max}
              onChange={(e) => update('threshold_max', e.target.value)}
              placeholder="Upper bound"
            />
          </div>
        </div>
        <p className="text-xs text-muted-foreground">
          Values outside the threshold range will require a comment in the daily log.
        </p>
      </CollapsibleContent>
    </Collapsible>
  );
}
